import { apiRequest } from '../../../services/api/client';

export type ClassLessonAttendanceRecord = {
  studentId: string;
  present: boolean;
  notes?: string | null;
};

type LessonResource = {
  id: number;
  class_lesson_id: number | null;
  lesson_number: number;
  title: string;
  summary: string | null;
  biblical_reference: string | null;
  taught_on: string | null;
  teacher_name: string | null;
  notes: string | null;
  bibles_count: number;
  offering_amount: number;
  is_missions_offering: boolean;
  has_attendance: boolean;
  present_count: number;
  absent_count: number;
  attendances: Array<{
    student_id: number;
    present: boolean;
    notes: string | null;
  }>;
};

type LessonListResponse = {
  data: LessonResource[];
};

type LessonResponse = {
  data: LessonResource;
};

export interface ClassLessonRecord {
  id: string;
  classLessonId?: string;
  classId: string;
  lessonNumber: number;
  title: string;
  date: string;
  description: string;
  teacherName?: string;
  hasAttendance: boolean;
  biblesCount: number;
  offeringAmount: number;
  isMissionsOffering: boolean;
  notes: string;
  presentCount: number;
  absentCount: number;
  studentAttendances: ClassLessonAttendanceRecord[];
}

export interface SyncClassLessonAttendanceInput {
  trimesterId: string;
  date: string;
  teacherName?: string;
  description?: string;
  biblesCount: number;
  offeringAmount: number;
  isMissionsOffering?: boolean;
  notes?: string;
  attendances: ClassLessonAttendanceRecord[];
}

const toLessonRecord = (classId: string, lesson: LessonResource): ClassLessonRecord => ({
  id: String(lesson.id),
  classLessonId: lesson.class_lesson_id ? String(lesson.class_lesson_id) : undefined,
  classId,
  lessonNumber: lesson.lesson_number,
  title: lesson.title,
  date: lesson.taught_on ?? '',
  description: lesson.summary ?? '',
  teacherName: lesson.teacher_name ?? undefined,
  hasAttendance: lesson.has_attendance,
  biblesCount: lesson.bibles_count ?? 0,
  offeringAmount: lesson.offering_amount ?? 0,
  isMissionsOffering: lesson.is_missions_offering ?? false,
  notes: lesson.notes ?? '',
  presentCount: lesson.present_count ?? 0,
  absentCount: lesson.absent_count ?? 0,
  studentAttendances: lesson.attendances.map((attendance) => ({
    studentId: String(attendance.student_id),
    present: attendance.present,
    notes: attendance.notes,
  })),
});

export const classLessonService = {
  async listByClass(classId: string, trimesterId: string): Promise<ClassLessonRecord[]> {
    const payload = await apiRequest<LessonListResponse>(`/ebd/classes/${classId}/lessons?quarter_id=${trimesterId}`);
    return payload.data.map((lesson) => toLessonRecord(classId, lesson));
  },

  async getAttendance(classId: string, lessonId: string, trimesterId: string): Promise<ClassLessonRecord> {
    const payload = await apiRequest<LessonResponse>(
      `/ebd/classes/${classId}/lessons/${lessonId}/attendance?quarter_id=${trimesterId}`
    );
    return toLessonRecord(classId, payload.data);
  },

  async syncAttendance(classId: string, lessonId: string, input: SyncClassLessonAttendanceInput): Promise<ClassLessonRecord> {
    const payload = await apiRequest<LessonResponse>(`/ebd/classes/${classId}/lessons/${lessonId}/attendance`, {
      method: 'PUT',
      body: JSON.stringify({
        quarter_id: Number(input.trimesterId),
        taught_on: input.date,
        teacher_name: input.teacherName?.trim() || null,
        summary: input.description?.trim() || null,
        bibles_count: input.biblesCount,
        offering_amount: input.offeringAmount,
        is_missions_offering: input.isMissionsOffering ?? false,
        notes: input.notes?.trim() || null,
        attendances: input.attendances.map((attendance) => ({
          student_id: Number(attendance.studentId),
          present: attendance.present,
          notes: attendance.notes ?? null,
        })),
      }),
    });

    return toLessonRecord(classId, payload.data);
  },
};
